export const CUSTOM_HTTP_ROUTE_PREFIX = "/.doorway/http/v1/actions" as const;

export const PUBLIC_CREDENTIAL_ACTION_RISKS = ["read", "mutation", "destructive", "unknown"] as const;
export type PublicCredentialActionRisk = (typeof PUBLIC_CREDENTIAL_ACTION_RISKS)[number];

export const PUBLIC_CREDENTIAL_SAFETY_STATES = [
  "active", "challenged", "throttled", "automatically_suspended", "owner_disabled", "credential_disabled",
] as const;
export type PublicCredentialSafetyState = (typeof PUBLIC_CREDENTIAL_SAFETY_STATES)[number];

export const PUBLIC_CREDENTIAL_RESERVATION_STATES = [
  "pending", "committed", "dispatched", "settled", "cancelled", "expired", "outcome_unknown",
] as const;
export type PublicCredentialReservationState = (typeof PUBLIC_CREDENTIAL_RESERVATION_STATES)[number];

export const PUBLIC_CREDENTIAL_RESERVATION_KINDS = ["request", "egress"] as const;
export type PublicCredentialReservationKind = (typeof PUBLIC_CREDENTIAL_RESERVATION_KINDS)[number];

export interface PublicCredentialPolicyLimits {
  readPerVisitorActionMinute: number;
  readPerProjectMinute: number;
  concurrentReadsPerProject: number;
  mutationPerVisitorActionMinute: number;
  mutationPerProjectMinute: number;
  concurrentMutationsPerProject: number;
  dailyReadDispatchLimit: number;
  dailyMutationDispatchLimit: number;
}

export const PUBLIC_CREDENTIAL_DEFAULT_LIMITS = {
  readPerVisitorActionMinute: 20,
  readPerProjectMinute: 300,
  concurrentReadsPerProject: 5,
  mutationPerVisitorActionMinute: 3,
  mutationPerProjectMinute: 30,
  concurrentMutationsPerProject: 3,
  dailyReadDispatchLimit: 10_000,
  dailyMutationDispatchLimit: 500,
} as const satisfies PublicCredentialPolicyLimits;

export const PUBLIC_CREDENTIAL_BUDGET_CEILINGS = {
  workspaceDailyReadDispatchLimit: 50_000,
  workspaceDailyMutationDispatchLimit: 2_500,
  sharedConnectionDailyReadDispatchLimit: 20_000,
  sharedConnectionDailyMutationDispatchLimit: 1_000,
  dailyReadDispatchLimit: 1_000_000,
  dailyMutationDispatchLimit: 25_000,
} as const;

export const PUBLIC_CREDENTIAL_BREAKER_DEFAULTS = {
  authenticationFailureThreshold: 5,
  authenticationFailureWindowSeconds: 300,
  unknownMutationThreshold: 3,
  unknownMutationWindowSeconds: 600,
  hardRejectChallengeThreshold: 100,
  hardRejectSuspendThreshold: 500,
  hardRejectWindowSeconds: 300,
  providerRateLimitSuspendThreshold: 20,
  providerRateLimitWindowSeconds: 300,
  authenticationCooldownSeconds: 900,
  rateLimitCooldownSeconds: 300,
} as const;

export const PUBLIC_SAFETY_CONNECTION_EDGE_DEFAULTS = {
  defaultsVersion: 1,
  readPerMinute: 120,
  mutationPerMinute: 12,
} as const;

export const PUBLIC_SAFETY_SCOPE_KINDS = ["platform", "workspace", "project", "action", "connection"] as const;
export type PublicSafetyScopeKind = (typeof PUBLIC_SAFETY_SCOPE_KINDS)[number];

export const PUBLIC_SAFETY_RESERVATION_SCOPE_ORDER = {
  platform: 0,
  workspace: 1,
  project: 2,
  action: 3,
  connection: 4,
} as const satisfies Readonly<Record<PublicSafetyScopeKind, number>>;

export const PUBLIC_SAFETY_SIGNAL_KINDS = [
  "authentication_failure", "unknown_mutation", "hard_limit_reject", "provider_rate_limited",
] as const;
export type PublicSafetySignalKind = (typeof PUBLIC_SAFETY_SIGNAL_KINDS)[number];

export const PUBLIC_SAFETY_TRIP_CAUSES = [
  "owner_kill", "operator_kill", "daily_budget_exhausted", "repeated_authentication_failure",
  "upstream_rate_limited", "anomaly_threshold", "repeated_unknown_mutation",
  "credential_disabled", "credential_rotated", "policy_changed", "revision_changed",
] as const;
export type PublicSafetyTripCause = (typeof PUBLIC_SAFETY_TRIP_CAUSES)[number];

export const PUBLIC_SAFETY_RUNTIME_ERROR_CODES = [
  "CHALLENGE_REQUIRED",
  "ACTION_DISABLED",
  "BUDGET_EXHAUSTED",
  "CONCURRENCY_EXHAUSTED",
  "SAFETY_SUSPENDED",
  "AUTHORITY_STALE",
  "INTERNAL_ERROR",
] as const;
export type PublicSafetyRuntimeErrorCode = (typeof PUBLIC_SAFETY_RUNTIME_ERROR_CODES)[number];

export interface PublicSafetyConnectionEdgeLimits {
  readPerMinute: number;
  mutationPerMinute: number;
}

export interface PublicSafetyActionDailyLimits {
  dailyReadDispatchLimit: number;
  dailyMutationDispatchLimit: number;
}

export interface PublicSafetyRequestEnvelope {
  schemaVersion: 1;
  requestReservationId: string;
  workspaceId: string;
  projectId: string;
  revisionId: string;
  policyVersionId: string;
  authorityGeneration: number;
  allowedActionsDigest: string;
  invocationDigest: string;
  credentialBindingsDigest: string;
  visitorAuthorityDigest: string;
  risk: "read" | "mutation";
  maxEgressCalls: number;
  expiresAt: number;
}

export interface PublicSafetyRuntimeError {
  schemaVersion: 1;
  code: PublicSafetyRuntimeErrorCode;
  correlationId: string;
  retryable: boolean;
  retryAfterSeconds?: number;
}

export class PublicCredentialPolicyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PublicCredentialPolicyError";
  }
}

export function isPublicCredentialRiskEligible(risk: PublicCredentialActionRisk): risk is "read" | "mutation" {
  return risk === "read" || risk === "mutation";
}

export function parsePublicCredentialPolicyLimits(value: unknown): PublicCredentialPolicyLimits {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new PublicCredentialPolicyError("Public credential limits must be an object.");
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(PUBLIC_CREDENTIAL_DEFAULT_LIMITS) as (keyof PublicCredentialPolicyLimits)[];
  if (Object.keys(record).some((key) => !keys.includes(key as keyof PublicCredentialPolicyLimits))) {
    throw new PublicCredentialPolicyError("Public credential limits contain an unsupported field.");
  }
  if (keys.some((key) => !Object.hasOwn(record, key))) {
    throw new PublicCredentialPolicyError("Public credential limits must include every required field.");
  }
  const parsed = {} as PublicCredentialPolicyLimits;
  for (const key of keys) {
    const limit = record[key];
    if (!Number.isSafeInteger(limit) || (limit as number) < 0) {
      throw new PublicCredentialPolicyError("Public credential limits must be non-negative safe integers.");
    }
    if ((limit as number) > PUBLIC_CREDENTIAL_DEFAULT_LIMITS[key]) {
      throw new PublicCredentialPolicyError("Public credential limits cannot exceed the platform ceiling.");
    }
    parsed[key] = limit as number;
  }
  return parsed;
}

function publicSafetyRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new PublicCredentialPolicyError(`${label} must be an object.`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new PublicCredentialPolicyError(`${label} must be a plain object.`);
  }
  return value as Record<string, unknown>;
}

function publicSafetyExactKeys(record: Record<string, unknown>, keys: readonly string[], label: string): void {
  const allowed = new Set(keys);
  if (Object.keys(record).some((key) => !allowed.has(key))) {
    throw new PublicCredentialPolicyError(`${label} contains an unsupported field.`);
  }
  if (keys.some((key) => !Object.hasOwn(record, key))) {
    throw new PublicCredentialPolicyError(`${label} must include every required field.`);
  }
}

function publicSafetyLimit(value: unknown, ceiling: number, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new PublicCredentialPolicyError(`${label} must be a non-negative safe integer.`);
  }
  if ((value as number) > ceiling) {
    throw new PublicCredentialPolicyError(`${label} cannot exceed the platform ceiling.`);
  }
  return value as number;
}

function publicSafetyIdentifier(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0 || value.length > 200) {
    throw new PublicCredentialPolicyError(`${label} must be a non-empty identifier no longer than 200 characters.`);
  }
  if (value !== value.trim()) {
    throw new PublicCredentialPolicyError(`${label} must not contain leading or trailing whitespace.`);
  }
  return value;
}

function publicSafetyDigest(value: unknown, label: string): string {
  if (typeof value !== "string" || !/^[a-f0-9]{64}$/.test(value)) {
    throw new PublicCredentialPolicyError(`${label} must be a lowercase SHA-256 digest.`);
  }
  return value;
}

export function parsePublicSafetyConnectionEdgeLimits(value: unknown): PublicSafetyConnectionEdgeLimits {
  const record = publicSafetyRecord(value, "Public safety connection edge limits");
  publicSafetyExactKeys(record, ["readPerMinute", "mutationPerMinute"], "Public safety connection edge limits");
  return {
    readPerMinute: publicSafetyLimit(
      record.readPerMinute,
      PUBLIC_SAFETY_CONNECTION_EDGE_DEFAULTS.readPerMinute,
      "Public safety connection read limit",
    ),
    mutationPerMinute: publicSafetyLimit(
      record.mutationPerMinute,
      PUBLIC_SAFETY_CONNECTION_EDGE_DEFAULTS.mutationPerMinute,
      "Public safety connection mutation limit",
    ),
  };
}

export function parsePublicSafetyActionDailyLimits(value: unknown): PublicSafetyActionDailyLimits {
  const record = publicSafetyRecord(value, "Public safety action daily limits");
  publicSafetyExactKeys(
    record,
    ["dailyReadDispatchLimit", "dailyMutationDispatchLimit"],
    "Public safety action daily limits",
  );
  return {
    dailyReadDispatchLimit: publicSafetyLimit(
      record.dailyReadDispatchLimit,
      PUBLIC_CREDENTIAL_DEFAULT_LIMITS.dailyReadDispatchLimit,
      "Public safety action daily read limit",
    ),
    dailyMutationDispatchLimit: publicSafetyLimit(
      record.dailyMutationDispatchLimit,
      PUBLIC_CREDENTIAL_DEFAULT_LIMITS.dailyMutationDispatchLimit,
      "Public safety action daily mutation limit",
    ),
  };
}

export function parsePublicSafetyRequestEnvelope(value: unknown): PublicSafetyRequestEnvelope {
  const envelope = publicSafetyRecord(value, "Public safety request envelope");
  const keys = [
    "schemaVersion", "requestReservationId", "workspaceId", "projectId", "revisionId", "policyVersionId",
    "authorityGeneration", "allowedActionsDigest", "invocationDigest", "credentialBindingsDigest",
    "visitorAuthorityDigest", "risk", "maxEgressCalls", "expiresAt",
  ] as const;
  publicSafetyExactKeys(envelope, keys, "Public safety request envelope");
  if (envelope.schemaVersion !== 1) {
    throw new PublicCredentialPolicyError("Public safety request envelope schemaVersion must equal 1.");
  }
  const authorityGeneration = envelope.authorityGeneration;
  if (!Number.isSafeInteger(authorityGeneration) || (authorityGeneration as number) < 1) {
    throw new PublicCredentialPolicyError("Public safety authority generation must be a positive safe integer.");
  }
  if (envelope.risk !== "read" && envelope.risk !== "mutation") {
    throw new PublicCredentialPolicyError("Public safety request risk must be read or mutation.");
  }
  const maxEgressCalls = publicSafetyLimit(envelope.maxEgressCalls, 64, "Public safety maximum egress calls");
  const expiresAt = envelope.expiresAt;
  if (!Number.isSafeInteger(expiresAt) || (expiresAt as number) < 1) {
    throw new PublicCredentialPolicyError("Public safety request expiry must be a positive safe integer.");
  }
  return {
    schemaVersion: 1,
    requestReservationId: publicSafetyIdentifier(envelope.requestReservationId, "Request reservation ID"),
    workspaceId: publicSafetyIdentifier(envelope.workspaceId, "Workspace ID"),
    projectId: publicSafetyIdentifier(envelope.projectId, "Project ID"),
    revisionId: publicSafetyIdentifier(envelope.revisionId, "Revision ID"),
    policyVersionId: publicSafetyIdentifier(envelope.policyVersionId, "Policy version ID"),
    authorityGeneration: authorityGeneration as number,
    allowedActionsDigest: publicSafetyDigest(envelope.allowedActionsDigest, "Allowed actions digest"),
    invocationDigest: publicSafetyDigest(envelope.invocationDigest, "Invocation digest"),
    credentialBindingsDigest: publicSafetyDigest(envelope.credentialBindingsDigest, "Credential bindings digest"),
    visitorAuthorityDigest: publicSafetyDigest(envelope.visitorAuthorityDigest, "Visitor authority digest"),
    risk: envelope.risk,
    maxEgressCalls,
    expiresAt: expiresAt as number,
  };
}

export function parsePublicSafetyRuntimeError(value: unknown): PublicSafetyRuntimeError {
  const error = publicSafetyRecord(value, "Public safety runtime error");
  const required = ["schemaVersion", "code", "correlationId", "retryable"] as const;
  const allowed = [...required, "retryAfterSeconds"];
  const allowedSet = new Set(allowed);
  if (Object.keys(error).some((key) => !allowedSet.has(key))) {
    throw new PublicCredentialPolicyError("Public safety runtime error contains an unsupported field.");
  }
  if (required.some((key) => !Object.hasOwn(error, key))) {
    throw new PublicCredentialPolicyError("Public safety runtime error must include every required field.");
  }
  if (error.schemaVersion !== 1) {
    throw new PublicCredentialPolicyError("Public safety runtime error schemaVersion must equal 1.");
  }
  if (!PUBLIC_SAFETY_RUNTIME_ERROR_CODES.includes(error.code as PublicSafetyRuntimeErrorCode)) {
    throw new PublicCredentialPolicyError("Public safety runtime error code is unsupported.");
  }
  if (typeof error.correlationId !== "string" || error.correlationId.length < 16 || error.correlationId.length > 200) {
    throw new PublicCredentialPolicyError("Public safety runtime correlation ID is invalid.");
  }
  if (typeof error.retryable !== "boolean") {
    throw new PublicCredentialPolicyError("Public safety runtime retryable flag is invalid.");
  }
  let retryAfterSeconds: number | undefined;
  if (error.retryAfterSeconds !== undefined) {
    if (!Number.isSafeInteger(error.retryAfterSeconds) || (error.retryAfterSeconds as number) < 0
      || (error.retryAfterSeconds as number) > 86_400) {
      throw new PublicCredentialPolicyError("Public safety runtime retry timing is invalid.");
    }
    retryAfterSeconds = error.retryAfterSeconds as number;
  }
  return {
    schemaVersion: 1,
    code: error.code as PublicSafetyRuntimeErrorCode,
    correlationId: error.correlationId,
    retryable: error.retryable,
    ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
  };
}

export const CUSTOM_HTTP_METHODS = ["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"] as const;
export type CustomHttpMethod = (typeof CUSTOM_HTTP_METHODS)[number];

export const CUSTOM_HTTP_RESPONSE_MODES = ["json", "text"] as const;
export type CustomHttpResponseMode = (typeof CUSTOM_HTTP_RESPONSE_MODES)[number];

export const CUSTOM_HTTP_EXECUTION_STATES = ["succeeded", "pending", "failed", "outcome_unknown"] as const;
export type CustomHttpExecutionState = (typeof CUSTOM_HTTP_EXECUTION_STATES)[number];

export const CUSTOM_HTTP_ERROR_CODES = [
  "INVALID_MANIFEST", "UNSUPPORTED_FIELD", "LIMIT_EXCEEDED", "UNSAFE_DESTINATION", "INVALID_TEMPLATE",
  "INVALID_INVOCATION", "IDEMPOTENCY_REQUIRED", "IDEMPOTENCY_CONFLICT", "INVALID_RESPONSE", "METHOD_NOT_ALLOWED",
  "JSON_REQUIRED", "API_SETUP_REQUIRED", "API_DISABLED",
  "ACCESS_REQUIRED", "RATE_LIMITED", "CONCURRENCY_LIMIT", "UPSTREAM_TIMEOUT", "UPSTREAM_REJECTED",
  "UPSTREAM_UNAVAILABLE", "UPSTREAM_RESPONSE_TOO_LARGE", "OUTCOME_UNKNOWN", "INTERNAL_ERROR",
] as const;
export type CustomHttpErrorCode = (typeof CUSTOM_HTTP_ERROR_CODES)[number];

export const CUSTOM_HTTP_LIMITS = {
  maxManifestBytes: 131_072,
  maxConnections: 16,
  maxActions: 64,
  maxInputsPerAction: 32,
  maxMappingsPerAction: 32,
  maxKeyCharacters: 64,
  maxPathCharacters: 2_048,
  maxHeaderNameCharacters: 128,
  maxHeaderValueCharacters: 4_096,
  maxResponseHeaders: 32,
  maxInputStringCharacters: 16_384,
  maxJsonInputBytes: 65_536,
  maxInvocationBytes: 131_072,
  maxTemplateDepth: 16,
  maxTemplateNodes: 1_024,
  minTimeoutMs: 100,
  maxTimeoutMs: 30_000,
  minResponseBytes: 1_024,
  maxResponseBytes: 1_048_576,
} as const;

export const CUSTOM_HTTP_DEFAULT_ACTION_LIMITS: CustomHttpActionLimits = {
  timeoutMs: 10_000,
  responseBytes: 262_144,
};

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };
export type CustomHttpJsonTemplate = JsonValue | { $input: string } | CustomHttpJsonTemplate[] | {
  [key: string]: CustomHttpJsonTemplate;
};

export type CustomHttpCredential =
  | { type: "bearer" }
  | { type: "basic"; password: "empty" | "secret" }
  | { type: "header"; name: string }
  | { type: "query"; name: string }
  | { type: "webhook"; expectedOrigin: string };

export interface CustomHttpConnection { key: string; credential: CustomHttpCredential }

export type CustomHttpInput =
  | { key: string; type: "string"; required?: boolean; maxLength?: number }
  | { key: string; type: "number"; required?: boolean; minimum?: number; maximum?: number }
  | { key: string; type: "boolean"; required?: boolean }
  | { key: string; type: "json"; required?: boolean; maxBytes?: number };

export interface CustomHttpInputMapping { name: string; input: string }
export type CustomHttpHeaderMapping = { name: string; input: string } | { name: string; value: string };

export type CustomHttpBody =
  | { mode: "json"; template: CustomHttpJsonTemplate }
  | { mode: "text"; input: string };

export interface CustomHttpActionLimits { timeoutMs: number; responseBytes: number }

export interface CustomHttpAction {
  key: string;
  connectionKey: string;
  risk: "read" | "mutation";
  method: CustomHttpMethod;
  origin: string;
  path: string;
  inputs: CustomHttpInput[];
  query?: CustomHttpInputMapping[];
  headers?: CustomHttpHeaderMapping[];
  body?: CustomHttpBody;
  response: { mode: CustomHttpResponseMode; headers?: string[] };
  limits?: CustomHttpActionLimits;
}

export interface CustomHttpManifest { version: 1; connections: CustomHttpConnection[]; actions: CustomHttpAction[] }
export interface CustomHttpInvocation { input: Record<string, JsonValue>; idempotencyKey?: string }
export interface CustomHttpExecutionError {
  code: CustomHttpErrorCode;
  message: string;
  retryable: boolean;
  correlationId?: string;
  retryAfterSeconds?: number;
}
export type CustomHttpResponseBody = { type: "json"; value: JsonValue } | { type: "text"; text: string };
export interface CustomHttpResponse {
  requestId: string;
  state: CustomHttpExecutionState;
  replayed: boolean;
  upstreamStatus?: number;
  headers?: Record<string, string>;
  body?: CustomHttpResponseBody;
  error?: CustomHttpExecutionError;
}

export class CustomHttpContractError extends Error {
  readonly code: CustomHttpErrorCode;
  constructor(code: CustomHttpErrorCode, message: string) {
    super(message);
    this.name = "CustomHttpContractError";
    this.code = code;
  }
}

export function canonicalCustomHttpJson(value: unknown): string {
  if (value === null || typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number" && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalCustomHttpJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${canonicalCustomHttpJson(entry)}`)
      .join(",")}}`;
  }
  throw new CustomHttpContractError("INVALID_MANIFEST", "The value must contain canonical JSON data.");
}

function invalid(code: CustomHttpErrorCode, message: string): never {
  throw new CustomHttpContractError(code, message);
}

function recordAt(value: unknown, code: CustomHttpErrorCode, message: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) invalid(code, message);
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) invalid(code, message);
  return value as Record<string, unknown>;
}

function exactKeys(record: Record<string, unknown>, allowed: readonly string[], code: CustomHttpErrorCode): void {
  const allowedKeys = new Set(allowed);
  if (Object.keys(record).some((key) => !allowedKeys.has(key))) invalid(code, "The value contains an unsupported field.");
}

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function jsonBytes(value: unknown, code: CustomHttpErrorCode): number {
  try {
    const encoded = JSON.stringify(value);
    if (encoded === undefined) invalid(code, "The value must be JSON serializable.");
    return utf8Bytes(encoded);
  } catch (caught) {
    if (caught instanceof CustomHttpContractError) throw caught;
    invalid(code, "The value must be JSON serializable.");
  }
}

const KEY_PATTERN = /^[a-z][a-z0-9_-]*$/;
const INPUT_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;
const HEADER_NAME_PATTERN = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;
const QUERY_NAME_PATTERN = /^[A-Za-z0-9_.~-]+$/;
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9._:-]+$/;
const FORBIDDEN_REQUEST_HEADERS = new Set([
  "authorization", "connection", "content-length", "cookie", "host", "origin", "proxy-authorization",
  "referer", "set-cookie", "transfer-encoding",
]);
const FORBIDDEN_RESPONSE_HEADERS = new Set(["authorization", "cookie", "proxy-authenticate", "set-cookie"]);
const RESERVED_HOST_SUFFIXES = [
  ".arpa", ".example", ".home", ".internal", ".invalid", ".lan", ".local", ".localdomain", ".localhost", ".onion", ".test",
];

function boundedKey(value: unknown, label: string, input = false): string {
  if (typeof value !== "string" || value.length === 0 || value.length > CUSTOM_HTTP_LIMITS.maxKeyCharacters
    || !(input ? INPUT_KEY_PATTERN : KEY_PATTERN).test(value)) {
    invalid("INVALID_MANIFEST", `${label} must be a valid identifier.`);
  }
  return value;
}

export function isCustomHttpActionKey(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= CUSTOM_HTTP_LIMITS.maxKeyCharacters
    && KEY_PATTERN.test(value);
}

function parseBooleanOption(value: unknown, label: string): boolean | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "boolean") invalid("INVALID_MANIFEST", `${label} must be a boolean.`);
  return value;
}

function safeHeaderName(value: unknown, credential = false): string {
  if (typeof value !== "string" || value.length === 0 || value.length > CUSTOM_HTTP_LIMITS.maxHeaderNameCharacters
    || !HEADER_NAME_PATTERN.test(value)) invalid("INVALID_TEMPLATE", "Header names must use valid HTTP token characters.");
  const normalized = value.toLowerCase();
  if (FORBIDDEN_REQUEST_HEADERS.has(normalized) && !(credential && normalized === "authorization")) {
    invalid("INVALID_TEMPLATE", `The ${value} header cannot be configured by an action.`);
  }
  return value;
}

function safeResponseHeaderName(value: unknown): string {
  const name = safeHeaderName(value);
  if (FORBIDDEN_RESPONSE_HEADERS.has(name.toLowerCase())) {
    invalid("INVALID_TEMPLATE", `The ${name} response header cannot be returned to an app.`);
  }
  return name.toLowerCase();
}

function safeQueryName(value: unknown): string {
  if (typeof value !== "string" || value.length === 0 || value.length > CUSTOM_HTTP_LIMITS.maxKeyCharacters
    || !QUERY_NAME_PATTERN.test(value)) invalid("INVALID_TEMPLATE", "Query parameter names must be fixed safe names.");
  return value;
}

function safePublicOrigin(value: unknown): string {
  if (typeof value !== "string" || value.length > 2_048) invalid("UNSAFE_DESTINATION", "Action origins must be bounded HTTPS origins.");
  let parsed: URL;
  try { parsed = new URL(value); } catch { invalid("UNSAFE_DESTINATION", "Action origins must be valid HTTPS origins."); }
  const hostname = parsed.hostname.toLowerCase().replace(/\.$/, "");
  const isIpv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  const isIpv6 = hostname.includes(":");
  const reserved = hostname === "localhost" || RESERVED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
  if (parsed.protocol !== "https:" || parsed.username !== "" || parsed.password !== "" || parsed.port !== ""
    || parsed.pathname !== "/" || parsed.search !== "" || parsed.hash !== "" || hostname.length === 0
    || !hostname.includes(".") || isIpv4 || isIpv6 || reserved || parsed.origin !== value) {
    invalid("UNSAFE_DESTINATION", "Action origins must be exact public HTTPS origins on port 443.");
  }
  return value;
}

function parseCredential(value: unknown): CustomHttpCredential {
  const credential = recordAt(value, "INVALID_MANIFEST", "A connection credential must be an object.");
  if (credential.type === "bearer") {
    exactKeys(credential, ["type"], "UNSUPPORTED_FIELD");
    return { type: "bearer" };
  }
  if (credential.type === "basic") {
    exactKeys(credential, ["type", "password"], "UNSUPPORTED_FIELD");
    if (credential.password !== "empty" && credential.password !== "secret") {
      invalid("INVALID_MANIFEST", "A basic credential must declare its password mode.");
    }
    return { type: "basic", password: credential.password };
  }
  if (credential.type === "header") {
    exactKeys(credential, ["type", "name"], "UNSUPPORTED_FIELD");
    return { type: "header", name: safeHeaderName(credential.name, true) };
  }
  if (credential.type === "query") {
    exactKeys(credential, ["type", "name"], "UNSUPPORTED_FIELD");
    return { type: "query", name: safeQueryName(credential.name) };
  }
  if (credential.type === "webhook") {
    exactKeys(credential, ["type", "expectedOrigin"], "UNSUPPORTED_FIELD");
    return { type: "webhook", expectedOrigin: safePublicOrigin(credential.expectedOrigin) };
  }
  invalid("INVALID_MANIFEST", "A connection has an unsupported credential type.");
}

function parseConnection(value: unknown): CustomHttpConnection {
  const connection = recordAt(value, "INVALID_MANIFEST", "Each connection must be an object.");
  exactKeys(connection, ["key", "credential"], "UNSUPPORTED_FIELD");
  return { key: boundedKey(connection.key, "Connection key"), credential: parseCredential(connection.credential) };
}

function positiveIntegerAtMost(value: unknown, maximum: number, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1 || (value as number) > maximum) {
    invalid("LIMIT_EXCEEDED", `${label} must be between 1 and ${maximum}.`);
  }
  return value as number;
}

function finiteNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) invalid("INVALID_MANIFEST", `${label} must be a finite number.`);
  return value;
}

function parseInput(value: unknown): CustomHttpInput {
  const input = recordAt(value, "INVALID_MANIFEST", "Each action input must be an object.");
  const key = boundedKey(input.key, "Input key", true);
  const required = parseBooleanOption(input.required, `Input ${key} required`);
  if (input.type === "string") {
    exactKeys(input, ["key", "type", "required", "maxLength"], "UNSUPPORTED_FIELD");
    const maxLength = input.maxLength === undefined ? undefined
      : positiveIntegerAtMost(input.maxLength, CUSTOM_HTTP_LIMITS.maxInputStringCharacters, `Input ${key} maxLength`);
    return { key, type: "string", ...(required === undefined ? {} : { required }), ...(maxLength === undefined ? {} : { maxLength }) };
  }
  if (input.type === "number") {
    exactKeys(input, ["key", "type", "required", "minimum", "maximum"], "UNSUPPORTED_FIELD");
    const minimum = input.minimum === undefined ? undefined : finiteNumber(input.minimum, `Input ${key} minimum`);
    const maximum = input.maximum === undefined ? undefined : finiteNumber(input.maximum, `Input ${key} maximum`);
    if (minimum !== undefined && maximum !== undefined && minimum > maximum) invalid("INVALID_MANIFEST", `Input ${key} minimum cannot exceed maximum.`);
    return { key, type: "number", ...(required === undefined ? {} : { required }), ...(minimum === undefined ? {} : { minimum }), ...(maximum === undefined ? {} : { maximum }) };
  }
  if (input.type === "boolean") {
    exactKeys(input, ["key", "type", "required"], "UNSUPPORTED_FIELD");
    return { key, type: "boolean", ...(required === undefined ? {} : { required }) };
  }
  if (input.type === "json") {
    exactKeys(input, ["key", "type", "required", "maxBytes"], "UNSUPPORTED_FIELD");
    const maxBytes = input.maxBytes === undefined ? undefined
      : positiveIntegerAtMost(input.maxBytes, CUSTOM_HTTP_LIMITS.maxJsonInputBytes, `Input ${key} maxBytes`);
    return { key, type: "json", ...(required === undefined ? {} : { required }), ...(maxBytes === undefined ? {} : { maxBytes }) };
  }
  invalid("INVALID_MANIFEST", `Input ${key} has an unsupported type.`);
}

function parseQueryMappings(value: unknown): CustomHttpInputMapping[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) invalid("INVALID_TEMPLATE", "Query mappings must be an array.");
  if (value.length > CUSTOM_HTTP_LIMITS.maxMappingsPerAction) invalid("LIMIT_EXCEEDED", "Query mappings exceed the supported limit.");
  const names = new Set<string>();
  return value.map((entry) => {
    const mapping = recordAt(entry, "INVALID_TEMPLATE", "Each Query mapping must be an object.");
    exactKeys(mapping, ["name", "input"], "UNSUPPORTED_FIELD");
    const name = safeQueryName(mapping.name);
    const normalizedName = name.toLowerCase();
    if (names.has(normalizedName)) invalid("INVALID_TEMPLATE", "Query mapping names must be unique.");
    names.add(normalizedName);
    return { name, input: boundedKey(mapping.input, "Query input", true) };
  });
}

function parseHeaderMappings(value: unknown): CustomHttpHeaderMapping[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) invalid("INVALID_TEMPLATE", "Header mappings must be an array.");
  if (value.length > CUSTOM_HTTP_LIMITS.maxMappingsPerAction) invalid("LIMIT_EXCEEDED", "Header mappings exceed the supported limit.");
  const names = new Set<string>();
  return value.map((entry) => {
    const mapping = recordAt(entry, "INVALID_TEMPLATE", "Each Header mapping must be an object.");
    const name = safeHeaderName(mapping.name);
    const normalizedName = name.toLowerCase();
    if (names.has(normalizedName)) invalid("INVALID_TEMPLATE", "Header mapping names must be unique.");
    names.add(normalizedName);
    if ("input" in mapping) {
      exactKeys(mapping, ["name", "input"], "UNSUPPORTED_FIELD");
      return { name, input: boundedKey(mapping.input, "Header input", true) };
    }
    exactKeys(mapping, ["name", "value"], "UNSUPPORTED_FIELD");
    if (typeof mapping.value !== "string" || mapping.value.length > CUSTOM_HTTP_LIMITS.maxHeaderValueCharacters || /\r|\n/.test(mapping.value)) {
      invalid("INVALID_TEMPLATE", "Fixed header values must be bounded text without line breaks.");
    }
    return { name, value: mapping.value };
  });
}

function parsePath(value: unknown, inputKeys: Set<string>, references: Set<string>, pathReferences: Set<string>): string {
  if (typeof value !== "string" || value.length === 0) invalid("INVALID_TEMPLATE", "Action paths must be non-empty text.");
  if (value.length > CUSTOM_HTTP_LIMITS.maxPathCharacters) invalid("LIMIT_EXCEEDED", "The action path is too long.");
  if (!value.startsWith("/") || value.startsWith("//") || /[?#\\\u0000-\u001f\u007f]/.test(value)) {
    invalid("INVALID_TEMPLATE", "Action paths must be fixed relative paths without a query or fragment.");
  }
  let decoded: string;
  try { decoded = decodeURIComponent(value); } catch { invalid("INVALID_TEMPLATE", "Action paths must contain valid percent encoding."); }
  if (decoded.split("/").some((segment) => segment === "." || segment === "..") || decoded.includes("\\")) {
    invalid("INVALID_TEMPLATE", "Action paths cannot contain traversal segments.");
  }
  const tokenPattern = /\{([A-Za-z][A-Za-z0-9_]*)\}/g;
  for (const match of value.matchAll(tokenPattern)) {
    const key = match[1]!;
    if (!inputKeys.has(key)) invalid("INVALID_TEMPLATE", `Path input ${key} is not declared.`);
    references.add(key);
    pathReferences.add(key);
  }
  const staticPath = value.replace(tokenPattern, "");
  if (staticPath.includes("{") || staticPath.includes("}")) invalid("INVALID_TEMPLATE", "Action paths contain an invalid input token.");
  return value;
}

function parseJsonTemplate(value: unknown, inputKeys: Set<string>, references: Set<string>, counter: { nodes: number }, depth = 0): CustomHttpJsonTemplate {
  counter.nodes += 1;
  if (depth > CUSTOM_HTTP_LIMITS.maxTemplateDepth || counter.nodes > CUSTOM_HTTP_LIMITS.maxTemplateNodes) {
    invalid("LIMIT_EXCEEDED", "The JSON body template is too complex.");
  }
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) invalid("INVALID_TEMPLATE", "JSON body template numbers must be finite.");
    return value;
  }
  if (Array.isArray(value)) return value.map((entry) => parseJsonTemplate(entry, inputKeys, references, counter, depth + 1));
  const object = recordAt(value, "INVALID_TEMPLATE", "JSON body templates must contain JSON values or input references.");
  if (Object.keys(object).length === 1 && "$input" in object) {
    const input = boundedKey(object.$input, "JSON template input", true);
    if (!inputKeys.has(input)) invalid("INVALID_TEMPLATE", `JSON body input ${input} is not declared.`);
    references.add(input);
    return { $input: input };
  }
  if ("$input" in object) invalid("INVALID_TEMPLATE", "$input objects cannot contain other fields.");
  const result: Record<string, CustomHttpJsonTemplate> = {};
  for (const [key, entry] of Object.entries(object)) {
    if (key.length === 0 || key.length > CUSTOM_HTTP_LIMITS.maxKeyCharacters || ["__proto__", "constructor", "prototype"].includes(key)) {
      invalid("INVALID_TEMPLATE", "JSON body template keys must be bounded safe names.");
    }
    result[key] = parseJsonTemplate(entry, inputKeys, references, counter, depth + 1);
  }
  return result;
}

function parseBody(value: unknown, inputKeys: Set<string>, references: Set<string>): CustomHttpBody | undefined {
  if (value === undefined) return undefined;
  const body = recordAt(value, "INVALID_TEMPLATE", "An action body must be an object.");
  if (body.mode === "json") {
    exactKeys(body, ["mode", "template"], "UNSUPPORTED_FIELD");
    return { mode: "json", template: parseJsonTemplate(body.template, inputKeys, references, { nodes: 0 }) };
  }
  if (body.mode === "text") {
    exactKeys(body, ["mode", "input"], "UNSUPPORTED_FIELD");
    const input = boundedKey(body.input, "Text body input", true);
    if (!inputKeys.has(input)) invalid("INVALID_TEMPLATE", `Text body input ${input} is not declared.`);
    references.add(input);
    return { mode: "text", input };
  }
  invalid("INVALID_TEMPLATE", "An action body must use json or text mode.");
}

function parseResponseDefinition(value: unknown): CustomHttpAction["response"] {
  const response = recordAt(value, "INVALID_MANIFEST", "An action response must be an object.");
  exactKeys(response, ["mode", "headers"], "UNSUPPORTED_FIELD");
  if (response.mode !== "json" && response.mode !== "text") invalid("INVALID_MANIFEST", "An action response mode must be json or text.");
  let headers: string[] | undefined;
  if (response.headers !== undefined) {
    if (!Array.isArray(response.headers)) invalid("INVALID_TEMPLATE", "Response headers must be an array.");
    if (response.headers.length > CUSTOM_HTTP_LIMITS.maxResponseHeaders) invalid("LIMIT_EXCEEDED", "An action declares too many response headers.");
    headers = response.headers.map(safeResponseHeaderName);
    if (new Set(headers).size !== headers.length) invalid("INVALID_TEMPLATE", "Response header names must be unique.");
  }
  return { mode: response.mode, ...(headers === undefined ? {} : { headers }) };
}

function parseActionLimits(value: unknown): CustomHttpActionLimits | undefined {
  if (value === undefined) return undefined;
  const limits = recordAt(value, "INVALID_MANIFEST", "Action limits must be an object.");
  exactKeys(limits, ["timeoutMs", "responseBytes"], "UNSUPPORTED_FIELD");
  if (!Number.isSafeInteger(limits.timeoutMs) || (limits.timeoutMs as number) < CUSTOM_HTTP_LIMITS.minTimeoutMs
    || (limits.timeoutMs as number) > CUSTOM_HTTP_LIMITS.maxTimeoutMs) invalid("LIMIT_EXCEEDED", "The action timeout is outside Doorway's supported range.");
  if (!Number.isSafeInteger(limits.responseBytes) || (limits.responseBytes as number) < CUSTOM_HTTP_LIMITS.minResponseBytes
    || (limits.responseBytes as number) > CUSTOM_HTTP_LIMITS.maxResponseBytes) invalid("LIMIT_EXCEEDED", "The action response limit is outside Doorway's supported range.");
  return { timeoutMs: limits.timeoutMs as number, responseBytes: limits.responseBytes as number };
}

function parseAction(value: unknown, connections: Map<string, CustomHttpConnection>): CustomHttpAction {
  const action = recordAt(value, "INVALID_MANIFEST", "Each action must be an object.");
  exactKeys(action, ["key", "connectionKey", "risk", "method", "origin", "path", "inputs", "query", "headers", "body", "response", "limits"], "UNSUPPORTED_FIELD");
  const key = boundedKey(action.key, "Action key");
  const connectionKey = boundedKey(action.connectionKey, "Action connection key");
  const connection = connections.get(connectionKey);
  if (!connection) invalid("INVALID_MANIFEST", `Action ${key} references an unknown connection.`);
  if (action.risk !== "read" && action.risk !== "mutation") invalid("INVALID_MANIFEST", `Action ${key} must declare read or mutation risk.`);
  if (!(CUSTOM_HTTP_METHODS as readonly unknown[]).includes(action.method)) invalid("INVALID_MANIFEST", `Action ${key} uses an unsupported method.`);
  const origin = safePublicOrigin(action.origin);
  if (connection.credential.type === "webhook" && connection.credential.expectedOrigin !== origin) {
    invalid("UNSAFE_DESTINATION", `Webhook action ${key} must use its connection's expected origin.`);
  }
  if (!Array.isArray(action.inputs)) invalid("INVALID_MANIFEST", `Action ${key} inputs must be an array.`);
  if (action.inputs.length > CUSTOM_HTTP_LIMITS.maxInputsPerAction) invalid("LIMIT_EXCEEDED", `Action ${key} declares too many inputs.`);
  const inputs = action.inputs.map(parseInput);
  const inputKeys = new Set(inputs.map((input) => input.key));
  if (inputKeys.size !== inputs.length) invalid("INVALID_MANIFEST", `Action ${key} input keys must be unique.`);
  const references = new Set<string>();
  const pathReferences = new Set<string>();
  const path = parsePath(action.path, inputKeys, references, pathReferences);
  const query = parseQueryMappings(action.query);
  const headers = parseHeaderMappings(action.headers);
  const mappedInputs = [
    ...(query ?? []),
    ...(headers?.filter((entry): entry is { name: string; input: string } => "input" in entry) ?? []),
  ];
  const inputDefinitions = new Map(inputs.map((input) => [input.key, input]));
  for (const mapping of mappedInputs) {
    if (!inputKeys.has(mapping.input)) invalid("INVALID_TEMPLATE", `Mapped input ${mapping.input} is not declared.`);
    references.add(mapping.input);
  }
  for (const pathInput of pathReferences) {
    const definition = inputDefinitions.get(pathInput)!;
    if (definition.required === false || definition.type === "json") {
      invalid("INVALID_TEMPLATE", `Path input ${pathInput} must be a required scalar.`);
    }
  }
  for (const mapping of mappedInputs) {
    if (inputDefinitions.get(mapping.input)!.type === "json") {
      invalid("INVALID_TEMPLATE", `Mapped input ${mapping.input} must be a scalar.`);
    }
  }
  if (connection.credential.type === "query") {
    const credentialQueryName = connection.credential.name;
    if (query?.some((mapping) => mapping.name === credentialQueryName)) {
      invalid("INVALID_TEMPLATE", `Action ${key} cannot override its credential query parameter.`);
    }
  }
  if (connection.credential.type === "webhook" && query && query.length > 0) {
    invalid("INVALID_TEMPLATE", `Webhook action ${key} cannot declare query mappings.`);
  }
  if (connection.credential.type === "header") {
    const credentialHeaderName = connection.credential.name.toLowerCase();
    if (headers?.some((mapping) => mapping.name.toLowerCase() === credentialHeaderName)) {
      invalid("INVALID_TEMPLATE", `Action ${key} cannot override its credential header.`);
    }
  }
  const body = parseBody(action.body, inputKeys, references);
  if ((action.method === "GET" || action.method === "HEAD") && body !== undefined) {
    invalid("INVALID_TEMPLATE", "GET and HEAD actions cannot declare a request body.");
  }
  if (inputs.some((input) => !references.has(input.key))) invalid("INVALID_TEMPLATE", `Every input for action ${key} must have a declared request destination.`);
  return {
    key, connectionKey, risk: action.risk, method: action.method as CustomHttpMethod, origin, path, inputs,
    ...(query === undefined ? {} : { query }), ...(headers === undefined ? {} : { headers }),
    ...(body === undefined ? {} : { body }), response: parseResponseDefinition(action.response),
    ...(action.limits === undefined ? {} : { limits: parseActionLimits(action.limits)! }),
  };
}

export function parseCustomHttpManifest(value: unknown): CustomHttpManifest {
  if (jsonBytes(value, "INVALID_MANIFEST") > CUSTOM_HTTP_LIMITS.maxManifestBytes) invalid("LIMIT_EXCEEDED", "The custom HTTP manifest is too large.");
  const manifest = recordAt(value, "INVALID_MANIFEST", "The custom HTTP manifest must be an object.");
  exactKeys(manifest, ["version", "connections", "actions"], "UNSUPPORTED_FIELD");
  if (manifest.version !== 1) invalid("INVALID_MANIFEST", "The custom HTTP manifest version must be 1.");
  if (!Array.isArray(manifest.connections) || manifest.connections.length === 0) invalid("INVALID_MANIFEST", "The custom HTTP manifest must declare at least one connection.");
  if (manifest.connections.length > CUSTOM_HTTP_LIMITS.maxConnections) invalid("LIMIT_EXCEEDED", "The custom HTTP manifest declares too many connections.");
  const connections = manifest.connections.map(parseConnection);
  const connectionMap = new Map(connections.map((connection) => [connection.key, connection]));
  if (connectionMap.size !== connections.length) invalid("INVALID_MANIFEST", "Connection keys must be unique.");
  if (!Array.isArray(manifest.actions)) invalid("INVALID_MANIFEST", "The custom HTTP manifest must declare an actions array.");
  if (manifest.actions.length > CUSTOM_HTTP_LIMITS.maxActions) invalid("LIMIT_EXCEEDED", "The custom HTTP manifest declares too many actions.");
  const actions = manifest.actions.map((action) => parseAction(action, connectionMap));
  if (new Set(actions.map((action) => action.key)).size !== actions.length) invalid("INVALID_MANIFEST", "Action keys must be unique.");
  return { version: 1, connections, actions };
}

function isJsonValue(value: unknown, depth = 0, nodes = { count: 0 }): value is JsonValue {
  nodes.count += 1;
  if (depth > CUSTOM_HTTP_LIMITS.maxTemplateDepth || nodes.count > CUSTOM_HTTP_LIMITS.maxTemplateNodes) return false;
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((entry) => isJsonValue(entry, depth + 1, nodes));
  if (typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (["__proto__", "constructor", "prototype"].includes(key) || !isJsonValue(entry, depth + 1, nodes)) return false;
  }
  return true;
}

function parseIdempotencyKey(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || value.length < 8 || value.length > 128 || !IDEMPOTENCY_KEY_PATTERN.test(value)) {
    invalid("INVALID_INVOCATION", "The idempotency key is invalid.");
  }
  return value;
}

function parseInvocationEnvelope(value: unknown): CustomHttpInvocation {
  if (jsonBytes(value, "INVALID_INVOCATION") > CUSTOM_HTTP_LIMITS.maxInvocationBytes) invalid("LIMIT_EXCEEDED", "The custom HTTP invocation is too large.");
  const invocation = recordAt(value, "INVALID_INVOCATION", "The custom HTTP invocation must be an object.");
  exactKeys(invocation, ["input", "idempotencyKey"], "UNSUPPORTED_FIELD");
  const input = recordAt(invocation.input, "INVALID_INVOCATION", "Invocation input must be an object.");
  for (const entry of Object.values(input)) {
    if (!isJsonValue(entry)) invalid("INVALID_INVOCATION", "Invocation inputs must contain bounded JSON values.");
  }
  const idempotencyKey = parseIdempotencyKey(invocation.idempotencyKey);
  return { input: input as Record<string, JsonValue>, ...(idempotencyKey === undefined ? {} : { idempotencyKey }) };
}

export function parseCustomHttpInvocationEnvelope(value: unknown): CustomHttpInvocation { return parseInvocationEnvelope(value); }

export function parseCustomHttpInvocation(value: unknown, action: CustomHttpAction): CustomHttpInvocation {
  const invocation = parseInvocationEnvelope(value);
  const definitions = new Map(action.inputs.map((input) => [input.key, input]));
  for (const key of Object.keys(invocation.input)) {
    if (!definitions.has(key)) invalid("INVALID_INVOCATION", `Input ${key} is not declared for this action.`);
  }
  const headerInputs = new Set(action.headers?.flatMap((mapping) => "input" in mapping ? [mapping.input] : []) ?? []);
  for (const definition of action.inputs) {
    const entry = invocation.input[definition.key];
    if (entry === undefined) {
      if (definition.required !== false) invalid("INVALID_INVOCATION", `Input ${definition.key} is required.`);
      continue;
    }
    if (definition.type === "string") {
      const maximum = definition.maxLength ?? CUSTOM_HTTP_LIMITS.maxInputStringCharacters;
      if (typeof entry !== "string" || entry.length > maximum) invalid("INVALID_INVOCATION", `Input ${definition.key} must be bounded text.`);
      if (headerInputs.has(definition.key) && (/\r|\n/.test(entry) || entry.length > CUSTOM_HTTP_LIMITS.maxHeaderValueCharacters)) {
        invalid("INVALID_INVOCATION", `Input ${definition.key} is not a safe header value.`);
      }
    } else if (definition.type === "number") {
      if (typeof entry !== "number" || !Number.isFinite(entry)
        || (definition.minimum !== undefined && entry < definition.minimum)
        || (definition.maximum !== undefined && entry > definition.maximum)) {
        invalid("INVALID_INVOCATION", `Input ${definition.key} is outside its numeric bounds.`);
      }
    } else if (definition.type === "boolean") {
      if (typeof entry !== "boolean") invalid("INVALID_INVOCATION", `Input ${definition.key} must be a boolean.`);
    } else {
      const maximum = definition.maxBytes ?? CUSTOM_HTTP_LIMITS.maxJsonInputBytes;
      if (!isJsonValue(entry) || jsonBytes(entry, "INVALID_INVOCATION") > maximum) invalid("INVALID_INVOCATION", `Input ${definition.key} exceeds its JSON limit.`);
    }
  }
  if (action.risk === "mutation" && invocation.idempotencyKey === undefined) invalid("IDEMPOTENCY_REQUIRED", "Mutation actions require an idempotency key.");
  return invocation;
}

function parseExecutionError(value: unknown): CustomHttpExecutionError {
  const error = recordAt(value, "INVALID_RESPONSE", "The execution error must be an object.");
  exactKeys(error, ["code", "message", "retryable", "correlationId", "retryAfterSeconds"], "INVALID_RESPONSE");
  if (!(CUSTOM_HTTP_ERROR_CODES as readonly unknown[]).includes(error.code)) invalid("INVALID_RESPONSE", "The execution error code is invalid.");
  if (typeof error.message !== "string" || error.message.length === 0 || error.message.length > 512) invalid("INVALID_RESPONSE", "The execution error message is invalid.");
  if (typeof error.retryable !== "boolean") invalid("INVALID_RESPONSE", "The execution retryable flag is invalid.");
  let correlationId: string | undefined;
  if (error.correlationId !== undefined) {
    if (typeof error.correlationId !== "string" || error.correlationId.length < 16 || error.correlationId.length > 200) {
      invalid("INVALID_RESPONSE", "The execution correlation ID is invalid.");
    }
    correlationId = error.correlationId;
  }
  let retryAfterSeconds: number | undefined;
  if (error.retryAfterSeconds !== undefined) {
    if (!Number.isSafeInteger(error.retryAfterSeconds) || (error.retryAfterSeconds as number) < 0
      || (error.retryAfterSeconds as number) > 86_400) {
      invalid("INVALID_RESPONSE", "The execution retry timing is invalid.");
    }
    retryAfterSeconds = error.retryAfterSeconds as number;
  }
  return {
    code: error.code as CustomHttpErrorCode,
    message: error.message,
    retryable: error.retryable,
    ...(correlationId === undefined ? {} : { correlationId }),
    ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
  };
}

function parseResponseBody(value: unknown): CustomHttpResponseBody {
  const body = recordAt(value, "INVALID_RESPONSE", "The response body must be an object.");
  if (body.type === "text") {
    exactKeys(body, ["type", "text"], "INVALID_RESPONSE");
    if (typeof body.text !== "string" || utf8Bytes(body.text) > CUSTOM_HTTP_LIMITS.maxResponseBytes) invalid("INVALID_RESPONSE", "The text response exceeds the supported limit.");
    return { type: "text", text: body.text };
  }
  if (body.type === "json") {
    exactKeys(body, ["type", "value"], "INVALID_RESPONSE");
    if (!isJsonValue(body.value) || jsonBytes(body.value, "INVALID_RESPONSE") > CUSTOM_HTTP_LIMITS.maxResponseBytes) invalid("INVALID_RESPONSE", "The JSON response is invalid or too large.");
    return { type: "json", value: body.value as JsonValue };
  }
  invalid("INVALID_RESPONSE", "The response body type is invalid.");
}

export function parseCustomHttpResponse(value: unknown): CustomHttpResponse {
  const response = recordAt(value, "INVALID_RESPONSE", "The custom HTTP response must be an object.");
  exactKeys(response, ["requestId", "state", "replayed", "upstreamStatus", "headers", "body", "error"], "INVALID_RESPONSE");
  if (typeof response.requestId !== "string" || response.requestId.length === 0 || response.requestId.length > 128) invalid("INVALID_RESPONSE", "The response request ID is invalid.");
  if (!(CUSTOM_HTTP_EXECUTION_STATES as readonly unknown[]).includes(response.state)) invalid("INVALID_RESPONSE", "The execution state is invalid.");
  if (typeof response.replayed !== "boolean") invalid("INVALID_RESPONSE", "The replay marker is invalid.");
  let upstreamStatus: number | undefined;
  if (response.upstreamStatus !== undefined) {
    if (!Number.isSafeInteger(response.upstreamStatus) || (response.upstreamStatus as number) < 100 || (response.upstreamStatus as number) > 599) {
      invalid("INVALID_RESPONSE", "The upstream status is invalid.");
    }
    upstreamStatus = response.upstreamStatus as number;
  }
  let headers: Record<string, string> | undefined;
  if (response.headers !== undefined) {
    const rawHeaders = recordAt(response.headers, "INVALID_RESPONSE", "Response headers must be an object.");
    if (Object.keys(rawHeaders).length > CUSTOM_HTTP_LIMITS.maxResponseHeaders) invalid("INVALID_RESPONSE", "The response contains too many headers.");
    headers = {};
    for (const [rawName, rawValue] of Object.entries(rawHeaders)) {
      const name = safeResponseHeaderName(rawName);
      if (typeof rawValue !== "string" || rawValue.length > CUSTOM_HTTP_LIMITS.maxHeaderValueCharacters || /\r|\n/.test(rawValue)) {
        invalid("INVALID_RESPONSE", "A response header value is invalid.");
      }
      headers[name] = rawValue;
    }
  }
  const body = response.body === undefined ? undefined : parseResponseBody(response.body);
  const error = response.error === undefined ? undefined : parseExecutionError(response.error);
  if (response.state === "succeeded") {
    if (error !== undefined) invalid("INVALID_RESPONSE", "Successful responses cannot contain execution errors.");
    if (body === undefined && response.replayed !== true) invalid("INVALID_RESPONSE", "Original successful responses must contain a body.");
  } else {
    if (body !== undefined) invalid("INVALID_RESPONSE", "Non-success responses cannot contain provider bodies.");
    if (response.state === "pending" && error !== undefined) invalid("INVALID_RESPONSE", "Pending responses cannot contain an error.");
    if ((response.state === "failed" || response.state === "outcome_unknown") && error === undefined) {
      invalid("INVALID_RESPONSE", "Terminal non-success responses require safe error metadata.");
    }
  }
  return {
    requestId: response.requestId, state: response.state as CustomHttpExecutionState, replayed: response.replayed,
    ...(upstreamStatus === undefined ? {} : { upstreamStatus }), ...(headers === undefined ? {} : { headers }),
    ...(body === undefined ? {} : { body }), ...(error === undefined ? {} : { error }),
  };
}
